import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  Output,
  ViewChild
} from '@angular/core';
import lodash, {Dictionary} from "lodash";
import {FacetValueResultFormatted} from "./facet-filter-value/facet-filter-value.component";
import {removeItem} from "../../utils";
import {FacetUtils, GetFacetValues} from "../facet-utils";
import {FacetValueResult} from "@flowmaps/flowmaps-typescriptmodels";
import {TranslateDirective} from "../../utils/translate.directive";
import {Handler} from "../../handler";
import {View} from "../../view";

@Component({
  selector: 'app-facet-filter',
  templateUrl: './facet-filter.component.html',
  styleUrls: ['./facet-filter.component.scss']
})
@Handler()
export class FacetFilterComponent extends View implements AfterViewInit {
  @Output() selectedValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Input() facets: FacetStats[];

  private ngZone = inject(NgZone);
  private _selectedValues: string[];
  private _hasValue: boolean;
  private _facet: FacetStats;
  protected facetValues: FacetValueResultFormatted[] = [];
  protected term: any;
  protected filteredValues: FacetValueResultFormatted[];
  private maxItems: number = 100;
  private valuesMap: Dictionary<FacetValueResultFormatted>;
  private from: number = 0;
  private endReached: boolean;
  protected facetsLoading: boolean;

  @ViewChild("dropdown") dropdown: ElementRef;
  @ViewChild("button") button: ElementRef;

  updateFilteredValues = (values: FacetValueResultFormatted[]) => {
    const selectedValues = this.selectedValues || [];
    this.filteredValues = this.valuesMap ? selectedValues.map(v => this.valuesMap[v]).concat(values.slice(0, this.from + this.maxItems)
      .filter(v => !selectedValues.includes(v.value))) : [];
  };

  ngAfterViewInit(): void {
    $(this.dropdown.nativeElement).on("show.bs.dropdown", () => {
      this.ngZone.run(() => {
        this.facetsLoading = true;
        this.sendQuery("getFacetValues", <GetFacetValues>{
          facetName: this.facet.name,
          allFacets: this.facets
        }).subscribe((v: FacetValueResult[]) => {
          this.facet.values = v;
          this.button.nativeElement.classList.add("active");
          this.setFacetValues();
          this.facetsLoading = false;
        })
        return true;
      });
    });
    $(this.dropdown.nativeElement).on("hide.bs.dropdown", () => {
      this.button.nativeElement.classList.remove("active");
      this.term = null;
      return true;
    });
  }

  @Input()
  set selectedValues(values: string[]) {
    values = (Array.isArray(values) ? values : [values]).filter(a => a);
    this._selectedValues = values;
    this._hasValue = values?.length > 0;
  }

  get selectedValues() {
    return this._selectedValues.filter(v => this.facet.values.find(va => va.value === v));
  }

  @Input()
  set facet(facet: FacetStats) {
    this._facet = facet;
    if (this.button?.nativeElement?.classList.contains("active")) {
      this.setFacetValues();
    } else {
      this.facetValues = [];
    }
    this._hasValue = (this._selectedValues || []).some(s => this.facet.values.some(v => v.value === s));
    this.setDefaultValueFormatter(facet);
  }

  private setFacetValues() {
    this.facetValues = this.formatFacetValues(this._facet);
    this.facetValues = lodash.sortBy(this.facetValues, f => f.formattedValue || f.value);
    this.valuesMap = lodash.keyBy(this.facetValues, (v) => v.value);
    this.updateFilteredValues(this.facetValues);
  }

  private formatFacetValues(facet: FacetStats) {
    if (!facet.valueFormatter) {
      return facet.values;
    }
    return (facet.values || []).map(f => {
      const facetFormatted = f as FacetValueResultFormatted;
      facetFormatted.formattedValue = this.facet.valueFormatter(f.value);
      return facetFormatted;
    });
  }

  get facet() {
    return this._facet;
  }

  get hasValue() {
    return this._hasValue;
  }

  protected removeFilter = () => {
    this.selectedValues = null;
    this.selectedValuesChange.emit(this.facet.valuesSelectedCallback
      ? this.facet.valuesSelectedCallback(this.facet, this.selectedValues) : this.selectedValues);
  }

  trackByFacetValue = (index: number, record: FacetValueResultFormatted) => record?.value;

  translateValue = (): boolean => this.facet.translateValues ? this.facet.translateValues(this.facet.name) : false;

  facetValueSelected(facetValue: FacetValueResultFormatted, selected: boolean) {
    const currentSelection = this.selectedValues || [];
    if (selected) {
      currentSelection.push(facetValue.value);
    } else {
      removeItem(currentSelection, facetValue.value);
    }
    this.selectedValues = currentSelection?.length > 0 ? currentSelection : null;
    this.selectedValuesChange.emit(this.facet.valuesSelectedCallback
      ? this.facet.valuesSelectedCallback(this.facet, this.selectedValues) : this.selectedValues);
  }

  getFacetName = (): string => this.facet.nameFormatter ? this.facet.nameFormatter(this.facet.name) : this.facet.name;

  get selectedValueFormatted() {
    return this.selectedValues
      ? this.facet.valueFormatter
        ? this.selectedValues.map(v => {
              const key = this.facet.valueFormatter(v);
              return this.translateValue() ? TranslateDirective.getTranslation(key, true) : key;
            }).join(", ")
        : this.selectedValues.map(v => this.translateValue() ? TranslateDirective.getTranslation(v, true) : v).join(", ")
      : null;
  }

  private setDefaultValueFormatter = (facet: FacetStats) => {
    if (facet.valueFormatter) {
      return;
    }
    if (FacetUtils.isBooleanFacet(facet)) {
      facet.valueFormatter = (value: string) => "true" === value ? "Yes" : "false" === value ? "No" : null;
    }
  }

  loadNextPage = () => {
    if (!this.endReached) {
      this.from += this.maxItems;
      this.updateFilteredValues(this.facetValues);
    }
  }
}

export interface FacetStats {
  name: string;
  values: FacetValueResult[];
  nameFormatter?: (name: string) => string;
  valueFormatter?: (value: string) => string;
  valuesSelectedCallback?: (facet: FacetStats, selectedValues: string[]) => string[];
  translateValues?: (name: string) => boolean;
}
